import { ReactNode } from "react";
import { useLocation } from "wouter";
import {
  Bug,
  ClipboardCheck,
  CreditCard,
  Laptop,
  LayoutDashboard,
  ListTodo,
  LogOut,
  MessageSquare,
  Search,
  Target,
  Users,
  Handshake,
  MoreHorizontal,
} from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";

interface AdminLayoutProps {
  children: ReactNode;
}

interface TabItem {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  group: "Control" | "Records" | "Operations";
}

const navItems: TabItem[] = [
  {
    title: "Desktop Control",
    url: "/admin/mission-control",
    icon: LayoutDashboard,
    group: "Control",
  },
  {
    title: "Conversations",
    url: "/admin/conversations",
    icon: MessageSquare,
    group: "Control",
  },
  {
    title: "Recordings",
    url: "/admin/search",
    icon: Search,
    group: "Control",
  },
  {
    title: "Clients",
    url: "/admin/clients",
    icon: Users,
    group: "Records",
  },
  {
    title: "Deals",
    url: "/admin/deals",
    icon: Handshake,
    group: "Records",
  },
  {
    title: "Bugs",
    url: "/admin/tickets",
    icon: Bug,
    group: "Operations",
  },
  {
    title: "Tasks",
    url: "/admin/tasks",
    icon: ListTodo,
    group: "Operations",
  },
  {
    title: "Financial",
    url: "/admin/billing",
    icon: CreditCard,
    group: "Operations",
  },
  {
    title: "Outreach",
    url: "/admin/outreach",
    icon: Target,
    group: "Operations",
  },
  {
    title: "Launch",
    url: "/admin/production-readiness",
    icon: ClipboardCheck,
    group: "Operations",
  },
  {
    title: "More",
    url: "/admin/more",
    icon: MoreHorizontal,
    group: "Operations",
  },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [location, navigate] = useLocation();
  const { toast } = useToast();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    toast({
      title: "Logged out",
      description: "You have been logged out successfully",
    });
    navigate("/admin/login");
  };

  const groupedNav = navItems.reduce<Record<TabItem["group"], TabItem[]>>(
    (groups, item) => {
      groups[item.group].push(item);
      return groups;
    },
    { Control: [], Records: [], Operations: [] },
  );

  return (
    <div className="admin-ui min-h-screen bg-slate-50 text-slate-950">
      <div className="flex min-h-screen items-center justify-center bg-white px-6 py-10 xl:hidden">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-slate-50 p-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-slate-950 text-white">
            <Laptop className="h-6 w-6" />
          </div>
          <h1 className="mt-5 text-2xl font-black tracking-tight text-slate-950">Desktop control only</h1>
          <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
            Use BetterSystems.ai admin from a desktop or laptop. Use the BSA Mission Control iPhone app for mobile owner work.
          </p>
          <button
            onClick={handleLogout}
            className="mt-6 inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </div>

      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 border-r border-slate-200 bg-white xl:flex xl:flex-col">
        <div className="flex h-16 items-center border-b border-slate-200 px-6">
          <div>
            <div className="text-sm font-black tracking-tight text-slate-950">Better Systems AI</div>
            <div className="text-xs font-medium text-slate-500">Desktop Control</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-4 py-5">
          {(["Control", "Records", "Operations"] as const).map((group) => (
            <div key={group} className="mb-6">
              <div className="mb-2 px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">
                {group}
              </div>
              <div className="space-y-1">
                {groupedNav[group].map((item) => {
                  const isActive = location === item.url || location.startsWith(item.url + "/");
                  const Icon = item.icon;

                  return (
                    <button
                      key={item.url}
                      onClick={() => navigate(item.url)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-semibold transition-colors",
                        isActive
                          ? "bg-slate-950 text-white"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-950",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{item.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </aside>

      <div className="hidden xl:block xl:pl-72">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6 xl:px-8">
            <div>
              <div className="text-sm font-semibold text-slate-600">BSA Desktop Control</div>
              <div className="text-xs font-medium text-slate-400">Full admin workspace</div>
            </div>
            <button
              onClick={handleLogout}
              className="hidden rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950 xl:flex xl:items-center xl:gap-2"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        </header>

        <main className="min-h-[calc(100vh-4rem)] overflow-auto">
          <div className="admin-content-shell w-full px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
